/* Winkelwagenlade zoals monavida.nl: "Add" bij de aanbevelingen (snippets/mv-lade-aanbeveling.liquid).
   Eén variant: direct in de wagen. Meer varianten: een kies-lade van onderen met foto, prijs, bespaar-badge, kleur als
   fotostaal en maat als blokje, plus "Add to cart" (NL: Impact quick-buy-drawer, gemeten 23 okt 2026). Toevoegen
   meldt zich bij Horizon met een CartLinesUpdateEvent, net als mv-bundels.js, zodat de lade en de teller verversen.
   USA 3 nov 2026: ook de knop van "Add 1 more and save" (snippets/mv-lade-meer.liquid): data-start kiest de kleur en
   maat van de regel vooraf, data-cadeau-variant voegt het verdiende cadeau mee toe (_mv_cadeau). En <mv-cadeau-wacht>
   (snippets/mv-cadeau-wacht.liquid) haalt een meegeleverd gratis cadeau weg dat niet meer verdiend is. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const routes = () => window.Theme?.routes || {};
const geld = (centen) => {
  const valuta = window.Shopify?.currency?.active || 'USD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: valuta }).format(centen / 100);
};
const KLEUR = /^(colou?r|kleur)$/i;

const secties = () => [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);

// het cadeau dat bij dit extra stuk hoort (wasnetje bij de 3-Pack); de kassa maakt het gratis, de wacht haalt het weg
// als de klant later weer onder het aantal zakt
const cadeauVan = (knop) => (knop?.dataset.cadeauVariant
  ? [{ id: Number(knop.dataset.cadeauVariant), quantity: 1, properties: { _mv_cadeau: `lade:${Date.now().toString(36)}` } }]
  : []);

async function voegToe(bron, variantId, extra = []) {
  const items = [{ id: Number(variantId), quantity: 1 }, ...extra];
  const uitgesteld = CartLinesUpdateEvent.createPromise();
  bron.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
    promise: uitgesteld.promise,
  }));
  try {
    const antwoord = await fetch(routes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: secties().join(',') }),
    }).then((r) => r.json());
    const wagen = await fetch(`${routes().cart_url || '/cart'}.js`).then((r) => r.json());
    if (antwoord.status) {
      bron.dispatchEvent(new CartErrorEvent({ error: antwoord.message || 'Add to cart failed', code: 'INVALID', detail: { description: antwoord.description } }));
      uitgesteld.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(wagen), detail: { didError: true, items: wagen.items, source: 'mv-lade' } });
      return false;
    }
    uitgesteld.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(wagen),
      detail: { items: wagen.items, source: 'mv-lade', itemCount: items.length, sections: antwoord.sections, didError: false },
    });
    return true;
  } catch (fout) {
    console.error('[mv-lade] toevoegen mislukt', fout);
    uitgesteld.reject(fout);
    bron.dispatchEvent(new CartErrorEvent({ error: fout?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    return false;
  }
}

/* ---------- kies-lade ---------- */
let kiezer;

function maakKiezer() {
  kiezer = document.createElement('dialog');
  kiezer.className = 'mv-kiezer';
  kiezer.setAttribute('aria-label', 'Choose options');
  kiezer.addEventListener('click', (e) => { if (e.target === kiezer) sluitKiezer(); });
  kiezer.addEventListener('cancel', (e) => { e.preventDefault(); sluitKiezer(); });
  document.body.appendChild(kiezer);
}

function sluitKiezer() {
  if (!kiezer?.open) return;
  kiezer.classList.add('mv-kiezer--dicht');
  setTimeout(() => { kiezer.close(); kiezer.classList.remove('mv-kiezer--dicht'); }, 200);
}

function escape(tekst) {
  return String(tekst).replace(/[&<>"']/g, (t) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[t]);
}

function beeld(src, breedte) {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  return `${url}${url.includes('?') ? '&' : '?'}width=${breedte}`;
}

async function openKiezer(handle, knop) {
  if (!kiezer) maakKiezer();
  knop.disabled = true;
  let product;
  try {
    product = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`).then((r) => r.json());
  } catch (fout) {
    console.error('[mv-lade] product laden mislukt', fout);
    knop.disabled = false;
    return;
  }
  knop.disabled = false;
  const start = knop.dataset.start && product.variants.find((v) => String(v.id) === knop.dataset.start && v.available);
  const keuze = [...(start || product.variants.find((v) => v.available) || product.variants[0]).options];

  const huidige = () => product.variants.find((v) => v.options.every((o, i) => o === keuze[i]));
  const fotoVoor = (optieIndex, waarde) => {
    const v = product.variants.find((x) => x.options[optieIndex] === waarde && x.featured_image);
    return v?.featured_image?.src;
  };

  const teken = () => {
    const v = huidige();
    const foto = v?.featured_image?.src || product.featured_image;
    const besparing = v && v.compare_at_price > v.price ? v.compare_at_price - v.price : 0;
    const opties = product.options.map((optie, i) => {
      const isKleur = KLEUR.test(optie.name) && optie.values.some((w) => fotoVoor(i, w));
      const waarden = optie.values.map((w) => {
        const gekozen = keuze[i] === w;
        const kan = product.variants.some((x) => x.available && x.options[i] === w && x.options.every((o, j) => j === i || o === keuze[j]));
        const inhoud = isKleur ? `<img src="${beeld(fotoVoor(i, w), 80)}" alt="" width="40" height="60" loading="lazy">` : `<span>${escape(w)}</span>`;
        return `<button type="button" class="mv-kiezer__${isKleur ? 'staal' : 'blok'}${kan ? '' : ' mv-kiezer--op'}" aria-pressed="${gekozen}" aria-label="${escape(w)}" data-optie="${i}" data-waarde="${escape(w)}">${inhoud}</button>`;
      }).join('');
      return `<fieldset class="mv-kiezer__optie"><legend><b>${escape(optie.name)}:</b> <span>${escape(keuze[i])}</span></legend><div class="mv-kiezer__waarden">${waarden}</div></fieldset>`;
    }).join('');
    kiezer.innerHTML = `
      <div class="mv-kiezer__paneel">
        <div class="mv-kiezer__product">
          ${foto ? `<img class="mv-kiezer__foto" src="${beeld(foto, 160)}" alt="" width="80" height="111">` : ''}
          <div class="mv-kiezer__tekst">
            <a class="mv-kiezer__titel" href="${escape(product.url)}">${escape(product.title)}</a>
            <span class="mv-kiezer__prijs">
              ${v && v.compare_at_price > v.price ? `<s class="mv-kiezer__oud">${geld(v.compare_at_price)}</s>` : ''}
              <span class="mv-kiezer__nu">${geld(v ? v.price : product.price)}</span>
            </span>
            ${besparing ? `<span class="mv-kiezer__badge">${escape(window.mvLade?.bespaar || 'Save')} ${geld(besparing)}</span>` : ''}
          </div>
        </div>
        <div class="mv-kiezer__keuzes">${opties}</div>
        <button type="button" class="mv-kiezer__knop" ${v?.available ? '' : 'disabled'}>${v?.available ? escape(window.mvLade?.toevoegen || 'Add to cart') : escape(window.mvLade?.uitverkocht || 'Sold out')}</button>
      </div>`;
    kiezer.querySelectorAll('[data-optie]').forEach((b) => b.addEventListener('click', () => {
      keuze[Number(b.dataset.optie)] = b.dataset.waarde;
      teken();
    }));
    kiezer.querySelector('.mv-kiezer__knop').addEventListener('click', async (e) => {
      const variant = huidige();
      if (!variant?.available) return;
      e.currentTarget.disabled = true;
      e.currentTarget.classList.add('mv-kiezer__knop--bezig');
      await voegToe(knop, variant.id, cadeauVan(knop));
      sluitKiezer();
    });
  };
  teken();
  kiezer.showModal();
}

document.addEventListener('click', async (e) => {
  const knop = e.target instanceof Element ? e.target.closest('[data-mv-lade-voeg]') : null;
  if (!knop) return;
  e.preventDefault();
  if (knop.dataset.variant) {
    knop.disabled = true;
    await voegToe(knop, knop.dataset.variant, cadeauVan(knop));
    knop.disabled = false;
  } else if (knop.dataset.handle) {
    openKiezer(knop.dataset.handle, knop);
  }
});

/* ---------- meegeleverd cadeau dat niet meer verdiend is ---------- */
// data-weg = {regelsleutel: nieuw aantal}, uitgerekend in snippets/mv-cadeau-wacht.liquid bij elke render van de lade
// of de winkelwagenpagina. Eén keer per opdracht: mislukt het, dan niet eindeloos opnieuw proberen.
let laatsteOpdracht = '';

async function haalCadeauWeg(bron, opdracht) {
  let updates;
  try {
    updates = JSON.parse(opdracht);
  } catch (fout) {
    console.error('[mv-lade] cadeau-opdracht onleesbaar', opdracht, fout);
    return;
  }
  const uitgesteld = CartLinesUpdateEvent.createPromise();
  bron.dispatchEvent(new CartLinesUpdateEvent({
    action: 'remove',
    context: 'cart',
    lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
    promise: uitgesteld.promise,
  }));
  try {
    const wagen = await fetch(routes().cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: secties().join(','), sections_url: window.location.pathname }),
    }).then((r) => r.json());
    if (wagen.status) throw new Error(wagen.description || wagen.message || 'Cart update failed');
    uitgesteld.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(wagen),
      detail: { items: wagen.items, itemCount: wagen.item_count, source: 'mv-cadeau-wacht', sections: wagen.sections, didError: false },
    });
  } catch (fout) {
    console.error('[mv-lade] cadeau weghalen mislukt', fout);
    uitgesteld.reject(fout);
    bron.dispatchEvent(new CartErrorEvent({ error: fout?.message || 'Cart update failed', code: 'SERVICE_UNAVAILABLE' }));
  }
}

if (!customElements.get('mv-cadeau-wacht')) {
  customElements.define('mv-cadeau-wacht', class extends HTMLElement {
    static observedAttributes = ['data-weg'];

    connectedCallback() { this.plan(); }

    attributeChangedCallback() { if (this.isConnected) this.plan(); }

    plan() {
      const opdracht = this.dataset.weg || '';
      if (!opdracht) { laatsteOpdracht = ''; return; }
      if (opdracht === laatsteOpdracht) return;
      laatsteOpdracht = opdracht;
      // na de lopende render van Horizon, niet er middenin
      setTimeout(() => haalCadeauWeg(this, opdracht), 0);
    }
  });
}
